import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from 'core/api/queryKeys';
import { useI18n } from 'core/i18n/I18nProvider';
import { paths } from 'core/routing/paths';
import { assessmentApi } from 'features/assessment-dashboard/assessment.api';
import { humanizeApiError } from 'core/errors/mapFieldErrors';
import { Badge, EmptyState, ErrorState, PageHeader, Skeleton } from 'shared/components/Feedback';
import { ListTable } from 'shared/components/ListTable';
import { detailsAction, RowActionsCell, RowActionsHeader } from 'shared/components/RowActionsMenu';
import { Pagination } from 'shared/components/Pagination';
import { ResultScoreBars } from 'shared/charts/ResultScoreBars';
import { SelectField, TextField } from 'shared/forms/Fields';
import { CanonicalKey, OrderedPatternTrail } from 'features/top-three-profiles/OrderedPatternTrail';
import { rankingFromPatterns, readAttemptTopThree } from 'features/top-three-profiles/topThreeProfiles';
import { formatDateTime, formatUserLabel, localizedName } from 'shared/utilities/format';
import { TagChip, TagChipRow } from 'features/result-content/TagChip';
import { localizedCopy, requiresSkillAdvice, typeLabelKey } from 'features/result-content/resultContent';

function buildAttemptQuery(filters) {
  const params = {
    page: filters.page,
    per_page: filters.per_page,
    user_id: filters.user_id,
    status: filters.status,
    version_id: filters.version_id,
    completed_from: filters.completed_from,
    completed_to: filters.completed_to,
  };
  if (filters.version_id && filters.top_pattern_id) {
    params.top_pattern_id = filters.top_pattern_id;
  }
  return params;
}

export function AttemptsPage() {
  const { t, locale, dir } = useI18n();
  const [filters, setFilters] = useState({
    page: 1,
    per_page: 15,
    user_id: '',
    status: '',
    version_id: '',
    completed_from: '',
    completed_to: '',
    top_pattern_id: '',
  });
  const queryFilters = buildAttemptQuery(filters);
  const versionsQuery = useQuery({ queryKey: queryKeys.versions, queryFn: ({ signal }) => assessmentApi.listVersions({ signal }) });
  const versionDetailQuery = useQuery({
    queryKey: queryKeys.version(filters.version_id),
    enabled: Boolean(filters.version_id),
    queryFn: ({ signal }) => assessmentApi.showVersion(filters.version_id, { signal }),
  });
  const query = useQuery({
    queryKey: queryKeys.attempts(queryFilters),
    queryFn: ({ signal }) => assessmentApi.listAttempts(queryFilters, { signal }),
  });
  const items = query.data?.items || [];
  const meta = query.data?.meta || {};
  const versionPatterns = versionDetailQuery.data?.patterns || [];

  return (
    <div>
      <PageHeader title={t('attempts.title')} />
      <div className="toolbar wrap">
        <TextField id="user-id" label={t('attempts.user')} value={filters.user_id} onChange={(value) => setFilters((current) => ({ ...current, user_id: value, page: 1 }))} />
        <SelectField
          id="status"
          label={t('attempts.status')}
          value={filters.status}
          onChange={(value) => setFilters((current) => ({ ...current, status: value, page: 1 }))}
          options={[
            { value: '', label: t('filters.all') },
            { value: 'in_progress', label: 'in_progress' },
            { value: 'completed', label: 'completed' },
          ]}
        />
        <SelectField
          id="version-id"
          label={t('results.version')}
          value={filters.version_id}
          onChange={(value) => setFilters((current) => ({ ...current, version_id: value, top_pattern_id: '', page: 1 }))}
          options={[{ value: '', label: t('filters.all') }, ...(versionsQuery.data?.items || []).map((item) => ({ value: String(item.id), label: `#${item.number}` }))]}
        />
        <SelectField
          id="top-pattern"
          label={t('attempts.topPattern')}
          value={filters.top_pattern_id}
          onChange={(value) => setFilters((current) => ({ ...current, top_pattern_id: value, page: 1 }))}
          options={[{ value: '', label: t('filters.all') }, ...versionPatterns.map((item) => ({ value: String(item.id), label: localizedName(item, locale) }))]}
          disabled={!filters.version_id}
        />
        <TextField id="from" label={t('attempts.completed')} type="date" value={filters.completed_from} onChange={(value) => setFilters((current) => ({ ...current, completed_from: value, page: 1 }))} />
        <TextField id="to" label=" " type="date" value={filters.completed_to} onChange={(value) => setFilters((current) => ({ ...current, completed_to: value, page: 1 }))} />
      </div>
      {query.isLoading ? <Skeleton rows={6} /> : null}
      {query.isError ? <ErrorState title={humanizeApiError(query.error, t)} onRetry={query.refetch} retryLabel={t('actions.retry')} /> : null}
      {!query.isLoading && items.length === 0 ? <EmptyState title={t('empty.attempts')} /> : (
        <ListTable caption={t('attempts.title')} stickyActions>
          <thead>
            <tr>
              <th>ID</th>
              <th>{t('attempts.user')}</th>
              <th>{t('attempts.status')}</th>
              <th>{t('results.version')}</th>
              <th>{t('attempts.progress')}</th>
              <th>{t('attempts.completed')}</th>
              <RowActionsHeader />
            </tr>
          </thead>
          <tbody>
            {items.map((attempt) => (
              <tr key={attempt.id}>
                <td>{attempt.id}</td>
                <td>{formatUserLabel(attempt.user, t)}</td>
                <td>{attempt.status}</td>
                <td>{attempt.version?.number ?? '—'}</td>
                <td>{attempt.answered_questions_count}/{attempt.total_questions_count}</td>
                <td>{formatDateTime(attempt.completed_at)}</td>
                <RowActionsCell
                  label={`${t('actions.rowActions')} #${attempt.id}`}
                  items={[detailsAction(paths.attemptDetail(attempt.id), t)]}
                />
              </tr>
            ))}
          </tbody>
        </ListTable>
      )}
      <Pagination
        page={meta.current_page || filters.page}
        lastPage={meta.last_page}
        total={meta.total}
        onPageChange={(page) => setFilters((current) => ({ ...current, page }))}
        label={t('pagination.page')}
        ofLabel={t('pagination.of')}
        previousLabel={t('pagination.previous')}
        nextLabel={t('pagination.next')}
        dir={dir}
      />
    </div>
  );
}

export function AttemptDetailPage() {
  const { id } = useParams();
  const { t, locale } = useI18n();
  const query = useQuery({
    queryKey: queryKeys.attempt(id),
    queryFn: ({ signal }) => assessmentApi.showAttempt(id, { signal }),
  });
  if (query.isLoading) return <Skeleton rows={6} />;
  if (query.isError) return <ErrorState title={humanizeApiError(query.error, t)} onRetry={query.refetch} retryLabel={t('actions.retry')} />;
  const attempt = query.data;
  const result = attempt.result;
  const deliveredSummary = attempt.delivered_personal_summary || null;
  const deliveredItems = [...(attempt.delivered_ordered_items || [])].sort((left, right) => (left.sort_order || 0) - (right.sort_order || 0));

  return (
    <div>
      <PageHeader title={`${t('attempts.title')} #${attempt.id}`} />
      <p>{t('attempts.user')}: {formatUserLabel(attempt.user, t)}</p>
      <p>{t('attempts.status')}: {attempt.status}</p>
      <p>{t('results.version')}: {attempt.version?.number}</p>
      <p>{t('attempts.progress')}: {attempt.answered_questions_count}/{attempt.total_questions_count}</p>
      <p>{t('attempts.started')}: {formatDateTime(attempt.started_at, locale)}</p>
      <p>{t('attempts.completed')}: {formatDateTime(attempt.completed_at, locale)}</p>
      <p className="muted">{t('attempts.selectedAnswersUnavailable')}</p>
      {result ? (
        <>
          {(() => {
            const topThree = readAttemptTopThree(result);
            const ranked = rankingFromPatterns(topThree.patterns);
            return (
              <>
                <p>
                  {t('attempts.matchedProfile')}: {topThree.profileId
                    ? <Link to={paths.topThreeProfileDetail(topThree.profileId)}>#{topThree.profileId}</Link>
                    : '—'}
                </p>
                <p>
                  <strong>{t('profiles.canonicalKey')}:</strong> <CanonicalKey value={topThree.canonicalKey} />
                </p>
                <OrderedPatternTrail
                  first={topThree.first}
                  second={topThree.second}
                  third={topThree.third}
                  locale={locale}
                  t={t}
                />
                <p>{t('attempts.tieBreaking')}: {topThree.tieBreakingApplied ? t('workbook.yes') : t('workbook.no')}</p>
                <p>{t('attempts.snapshotRevision')}: {topThree.snapshotRevision ?? attempt.content_snapshot_revision ?? '—'}</p>
                <h3>{t('attempts.orderedRanking')}</h3>
                <ol className="pattern-ranking" data-testid="attempt-ranking">
                  {ranked.map((pattern) => (
                    <li key={pattern.id || pattern.pattern_id || `${pattern.rank}-${pattern.name_en}`}>
                      {localizedName(pattern, locale)} · {pattern.percentage ?? pattern.score ?? '—'}%
                      {pattern.rank != null ? ` · ${t('attempts.rank')} ${pattern.rank}` : ''}
                    </li>
                  ))}
                </ol>
                <ResultScoreBars patterns={topThree.patterns} locale={locale} tiedLabel={t('results.tied')} />
              </>
            );
          })()}
        </>
      ) : null}
      <section>
        <h2>{t('attempts.historicalContent')}</h2>
        <p className="muted">{t('attempts.snapshotNote')}</p>
        <h3>{t('attempts.deliveredSummary')}</h3>
        {deliveredSummary ? (
          <article className="card">
            <h4>{localizedCopy(deliveredSummary, locale)}</h4>
            <p>{localizedCopy(deliveredSummary, locale, 'description')}</p>
            <TagChipRow tags={deliveredSummary.tags || []} locale={locale} t={t} />
          </article>
        ) : (
          <p className="muted">{t('connections.noSummary')}</p>
        )}
        <h3>{t('attempts.deliveredItems')}</h3>
        {deliveredItems.length === 0 ? <p className="muted">{t('connections.noItems')}</p> : deliveredItems.map((item) => (
          <article key={item.id || item.source_result_item_id || `${item.type}-${item.sort_order}`} className="card">
            <Badge>{t(typeLabelKey(item.type))}</Badge>
            <h4>{localizedCopy(item, locale)}</h4>
            <p>{localizedCopy(item, locale, 'description')}</p>
            <TagChip tag={item.tag} locale={locale} t={t} />
            {requiresSkillAdvice(item.type) && (item.advice || item.advice_ar || item.advice_en) ? (
              <p><strong>{t('resultItems.skillAdvice')}:</strong> {localizedCopy(item, locale, 'advice')}</p>
            ) : null}
          </article>
        ))}
      </section>
    </div>
  );
}
